/**
 * Bottom sheet for adding a goal entry (numeric mode).
 */

import { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { X, Send } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { useGoalsStore } from '@turnifly/shared/stores'
import { useGoal, useCreateEntry } from '@turnifly/shared/hooks'
import { triggerHapticImpact as hapticImpact } from '@turnifly/shared/utils'

export function GoalEntrySheet(): React.ReactElement | null {
  const { t } = useTranslation()
  const { isEntrySheetOpen, entryGoalId, closeEntrySheet } = useGoalsStore()
  const { data: goal } = useGoal(entryGoalId)
  const createEntry = useCreateEntry()

  const [note, setNote] = useState('')
  const [value, setValue] = useState('')
  const [entryDate, setEntryDate] = useState(
    () => new Date().toISOString().split('T')[0],
  )
  const [error, setError] = useState<string | null>(null)

  const resetForm = useCallback((): void => {
    setNote('')
    setValue('')
    setEntryDate(new Date().toISOString().split('T')[0])
    setError(null)
  }, [])

  const handleClose = useCallback((): void => {
    closeEntrySheet()
    resetForm()
  }, [closeEntrySheet, resetForm])

  const handleSubmit = useCallback((): void => {
    if (!goal || !entryGoalId) return

    const trimmedNote = note.trim()
    if (!trimmedNote) {
      setError(t('goals.entryNoteRequired'))
      return
    }

    const numValue = Number(value) || 1

    if (numValue <= 0) {
      setError(t('goals.entryValueRequired'))
      return
    }

    hapticImpact('light')
    createEntry.mutate(
      {
        goal_id: entryGoalId,
        value: numValue,
        note: trimmedNote,
        entry_date: entryDate,
      },
      {
        onSuccess: handleClose,
        onError: () => setError(t('goals.entryError')),
      },
    )
  }, [goal, entryGoalId, note, value, entryDate, createEntry, handleClose, t])

  return (
    <AnimatePresence>
      {isEntrySheetOpen && goal && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm"
          />

          {/* Sheet */}
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 28, stiffness: 300 }}
            className="fixed inset-x-0 bottom-0 z-50 rounded-t-2xl border-t border-white/[0.08] bg-[var(--color-bg)] px-4 pb-8 pt-4"
          >
            {/* Header */}
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-base font-display font-bold text-text">
                {t('goals.addEntry')}
              </h3>
              <button
                type="button"
                onClick={handleClose}
                className="rounded-xl p-1.5 text-text-hint hover:bg-white/[0.06] tap-highlight-transparent"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Goal badge */}
            <div className="mb-4 flex items-center gap-2 rounded-xl bg-white/[0.04] border border-white/[0.06] px-3 py-2.5">
              <span className="text-lg">{goal.icon}</span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-text">{goal.name}</p>
                <p className="text-xs text-text-hint">
                  {goal.current_value}/{goal.target_value} {goal.unit}
                </p>
              </div>
            </div>

            {/* Note input */}
            <div className="mb-3">
              <label className="mb-1.5 block text-xs font-medium text-text-hint">
                {t('goals.entryNote')}
              </label>
              <input
                type="text"
                value={note}
                onChange={(e) => {
                  setNote(e.target.value)
                  setError(null)
                }}
                placeholder={t('goals.entryNotePlaceholder')}
                maxLength={300}
                className="w-full rounded-xl border border-white/[0.08] bg-white/[0.06] px-4 py-3 text-sm text-text placeholder:text-text-hint/40 outline-none transition-all focus:ring-1 focus:ring-primary/20 focus:border-primary/50"
                autoFocus
              />
            </div>

            {/* Value + Date row */}
            <div className="mb-4 grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1.5 block text-xs font-medium text-text-hint">
                  {t('goals.entryValue')} ({goal.unit})
                </label>
                <input
                  type="number"
                  inputMode="decimal"
                  value={value}
                  onChange={(e) => {
                    setValue(e.target.value)
                    setError(null)
                  }}
                  placeholder="1"
                  min={0}
                  className="w-full rounded-xl border border-white/[0.08] bg-white/[0.06] px-4 py-3 text-sm text-text placeholder:text-text-hint/40 outline-none transition-all focus:ring-1 focus:ring-primary/20 focus:border-primary/50"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-medium text-text-hint">
                  {t('goals.entryDate')}
                </label>
                <input
                  type="date"
                  value={entryDate}
                  onChange={(e) => setEntryDate(e.target.value)}
                  className="w-full rounded-xl border border-white/[0.08] bg-white/[0.06] px-4 py-3 text-sm text-text [color-scheme:dark] outline-none transition-all focus:ring-1 focus:ring-primary/20 focus:border-primary/50"
                />
              </div>
            </div>

            {/* Error */}
            {error && (
              <p className="mb-3 text-center text-xs text-danger">{error}</p>
            )}

            {/* Submit */}
            <button
              type="button"
              onClick={handleSubmit}
              disabled={createEntry.isPending}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary py-3.5 font-medium text-white transition-opacity disabled:opacity-50"
            >
              <Send className="h-4 w-4" />
              {t('goals.addEntry')}
            </button>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
